import ExifReader from 'exifreader'
import { loadImage } from 'mixtiles-web-common/services/imageLoader'
import { logger } from '../services/logger'
import {
  getPrintedImageDimensions,
  CANVAS_FRONT_DIMENSIONS,
  MATERIAL_TYPES,
} from '@mixtiles/web-backend-shared'

export const SUPPORTED_IMAGE_FORMATS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/svg+xml': ['.svg'],
}

export const DEFAULT_IMAGE_DPI = 300

export async function getImageDataByUrl(url) {
  const loadedImage = await loadImage(url)
  return {
    image: loadedImage,
    width: loadedImage.width,
    height: loadedImage.height,
  }
}

export function tileSizeToImageDimensions({
  tileSize,
  materialType,
  dpi = DEFAULT_IMAGE_DPI,
}) {
  if (materialType === MATERIAL_TYPES.CANVAS) {
    const { width, height } = CANVAS_FRONT_DIMENSIONS[tileSize]
    return {
      width: width * dpi,
      height: height * dpi,
    }
  } else {
    const { jigImageWidth, jigImageHeight } = getPrintedImageDimensions({
      tileSize,
      materialType,
    })
    return {
      width: jigImageWidth * dpi,
      height: jigImageHeight * dpi,
    }
  }
}

function disposeImage(image) {
  image.src = ''
  image = null
}

export async function preloadImagePromise(imageUrl, crossOrigin = false) {
  let loaded = false
  return new Promise((resolve) => {
    const img = document.createElement('img')
    if (crossOrigin) {
      img.crossOrigin = 'anonymous'
    }
    img.onload = () => {
      loaded = true
      disposeImage(img)
      resolve()
    }
    img.onerror = () => {
      if (!loaded) {
        disposeImage(img)
        resolve()
      }
    }
    img.src = imageUrl
  })
}

// Exif reader returns the format of the date as 'YYYY:MM:DD HH:mm:ss' and it needs to be converted to ISO format like this
// 'YYYY-MM-DDTHH:mm:ss'
function parseExifDate(dateStr) {
  const [day, time] = dateStr.split(' ')
  // Replace the first two colons with hyphens and add 'T' between date and time
  const formattedDay = day.replace(/:/g, '-')
  const formattedDateStr = `${formattedDay}T${time}`

  const date = new Date(formattedDateStr).getTime()
  if (isNaN(date)) {
    return undefined
  }
  return date
}

// Date formats:
// • 20261017
// • 2026-10-17
// • 2026_10_17
// • 2026.10.17
// Optional time formats:
// • _221532 (camera)
// • " at 22.15.32" (WhatsApp)
// • T221532 (ISO)
// • 22:15:32
// • 22-15-32
// • " at 2.15.32 PM" (macOS screenshot with 12h format)
const DATE_TIME_IN_FILENAME_REGEX =
  /(?<year>20\d{2})[-_.]?(?<month>[01]\d)[-_.]?(?<day>[0-3]\d)(?:[-_ T]+(?:at[-_ .]+)?(?<hours>[0-2]\d|\d(?=[-_.:]))[-_.:]?(?<minutes>[0-5]\d)[-_.:]?(?<seconds>[0-5]\d)(?:[-_ .]?(?<ampm>[AaPp][Mm]))?)?/

export function getDateFromFileName(fileName) {
  if (!fileName) {
    return undefined
  }

  const match = fileName.match(DATE_TIME_IN_FILENAME_REGEX)
  if (!match) {
    return undefined
  }

  const { year, month, day, hours, minutes, seconds, ampm } = match.groups
  // Doesn't check per-month day limits (e.g. Feb 31)
  // Camera-generated filenames won't have impossible dates
  if (+month < 1 || +month > 12 || +day < 1 || +day > 31) {
    return undefined
  }

  let h = hours != null ? +hours : null
  const hasTime = h != null && h <= 23 && +minutes <= 59 && +seconds <= 59

  if (hasTime && ampm) {
    const isPM = ampm[0] === 'P' || ampm[0] === 'p'
    if (isPM && h < 12) h += 12
    if (!isPM && h === 12) h = 0
  }

  const timestamp = hasTime
    ? new Date(+year, +month - 1, +day, h, +minutes, +seconds).getTime()
    : new Date(+year, +month - 1, +day).getTime()

  return { timestamp, hasTime }
}

/**
 * This function returns the date from the exif data of the image. If the exif data is not available, it returns the
 * last modified date of the image file.
 * @param file
 * @returns {Promise<number|undefined>}
 */
export async function getExifDate(file) {
  return (await getExifMetadata(file)).dateTaken
}

export function extractGpsFromTags(tags) {
  const lat = tags?.GPSLatitude?.description
  const lng = tags?.GPSLongitude?.description
  const latRef = tags?.GPSLatitudeRef?.value?.[0]
  const lngRef = tags?.GPSLongitudeRef?.value?.[0]

  if (lat == null || lng == null) return undefined

  const latitude = latRef === 'S' ? -lat : Number(lat)
  const longitude = lngRef === 'W' ? -lng : Number(lng)

  if (isNaN(latitude) || isNaN(longitude)) return undefined

  return { latitude, longitude }
}

/**
 * Extracts both date and GPS coordinates from EXIF data in a single read.
 * @param file
 * @returns {Promise<{dateTaken: number|undefined, gps: {latitude: number, longitude: number}|undefined}>}
 */
export async function getExifMetadata(file) {
  let tags
  try {
    tags = await ExifReader.load(file)
    const dateTaken = tags?.DateTimeOriginal?.description
      ? parseExifDate(tags.DateTimeOriginal.description)
      : undefined
    const gps = extractGpsFromTags(tags)
    return { dateTaken, gps }
  } catch (error) {
    logger.error('getExifMetadata: Error on load exif data', error)
  } finally {
    tags = null
  }

  return { dateTaken: undefined, gps: undefined }
}
